/* eslint-disable max-len, no-mixed-operators, prefer-arrow-callback, valid-jsdoc */
import { ChevronRight } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccordionDetails, AccordionSummary, Box, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import getViewsAbbreviated from '@zefr/style-guide/dist/ui/utils/strings/getViewsAbbreviated';
import React, { useMemo } from 'react';
import { RISK_LEVEL_MAP } from '../../../../atoms/contentAlignmentAtom';
import { useDemoForm } from '../../../../context/DemoFormContext';
import AverageDailyViewsGridItem from '../../../ChannelResults/AverageDailyViewsGridItem';
import ConceptsBox from '../../../ChannelResults/ConceptsBox';
import FakeLinearProgressBar from '../../../ChannelResults/FakeLinearProgressBar';
import RiskGridItem from '../../../ChannelResults/RiskGridItem';
import {
  PaddedFormControl,
  SummaryAccordion,
  SummaryItems,
} from '../../../ChannelResults/components';
import InternalIcon from '../../../InternalIcon';
import MaxLogoIcon from '../../../MaxLogoIcon';
import VideoTable from '../../../VideoTable';
import DownloadListButton from '../DownloadListButton';
import PrimarySortPicker from '../PrimarySortPicker';
import ChannelResultsHeader from '../StepInclusionResults/ChannelResultsHeader';
import { FormStepProps } from '../types';

// On xs, fit AvgDV and Risk side by side (6 + 6) so the user can see both
// metrics without scrolling. Same 6/6 split on md+ as before.
const itemGridSize = {
  xs: 6,
  md: 6,
};

export const StepExclusionResults: React.FC<FormStepProps> = ({
  step: myStep = 5,
  'data-testid': testId,
}) => {
  const {
    step,
    selectedPlatform,
    exclusionResults,
    riskLevel,
    listType,
    isExclusionResultLoading,
    isExclusionResultFetching,
    isExclusionResultSuccess,
  } = useDemoForm();
  /* #region 🐙 States */
  const riskLevelTitle = useMemo(() => {
    return typeof riskLevel !== 'undefined' && riskLevel >= 0
      ? Object.values(RISK_LEVEL_MAP)[riskLevel]
      : 'Unknown';
  }, [riskLevel]);

  const isLoading = useMemo(
    () => isExclusionResultLoading || isExclusionResultFetching,
    [isExclusionResultFetching, isExclusionResultLoading],
  );
  const isSuccess = useMemo(() => isExclusionResultSuccess, [isExclusionResultSuccess]);

  /* #endregion */
  /* #region 🎃 Events Handlers */
  if (step !== myStep || listType !== 'exclusion') {
    return null;
  }

  /* #endregion */
  return (
    <PaddedFormControl fullWidth focused={true} data-testid={testId}>
      <Grid
        container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: 2, md: 10 },
          boxSizing: 'border-box',
        }}
      >
        <Grid id="projected-performance-summary" xs={12}>
          <SummaryAccordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon fontSize="large" />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Box
                display={'flex'}
                flexDirection={'row'}
                justifyContent={'center'}
                alignItems={'center'}
                gap={8}
                flexWrap={'wrap'}
              >
                <Typography
                  variant="h6"
                  sx={(theme) => ({
                    color: theme.palette.secondary.main,
                  })}
                  fontWeight={'700'}
                  textAlign={'center'}
                  color="inherit"
                >
                  Projected Performance Summary
                </Typography>
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  justifyContent={'center'}
                  alignItems={'stretch'}
                  gap={3}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      height: 'unset',
                    },
                  }}
                >
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'flex-start'}
                    boxSizing={'border-box'}
                  >
                    <InternalIcon
                      sx={{ fontSize: '3.75rem' }}
                      iconName={selectedPlatform?.logoIcon}
                    />
                  </Box>
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'flex-start'}
                    boxSizing={'border-box'}
                  >
                    <Divider orientation={'vertical'} />
                  </Box>
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'flex-start'}
                    gap={1}
                    boxSizing={'border-box'}
                    p={1}
                  >
                    <Box
                      display={'flex'}
                      flexDirection={'row'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      gap={1}
                    >
                      <InternalIcon
                        sx={{ fontSize: '3.75rem' }}
                        iconName={selectedPlatform?.labelIcon}
                      />
                      {/* <Typography variant="h6" fontWeight={'bold'} color="inherit">
                        {selectedPlatform?.label}
                      </Typography> */}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <SummaryItems container gap={3} id="project-performance-summary-splitter">
                <SummaryItems
                  xs={true}
                  rowGap={1}
                  container
                  columnGap={3}
                  id="project-performance-summary-items"
                >
                  <Grid container gap={3} width={'100%'}>
                    <Grid xs={12} container gap={{ xs: 0.5, md: 3 }} sx={{ flexWrap: 'nowrap' }}>
                      <AverageDailyViewsGridItem
                        {...itemGridSize}
                        value={
                          isLoading ? (
                            <FakeLinearProgressBar />
                          ) : isSuccess ? (
                            exclusionResults?.aggregates.total_average_daily_views
                          ) : (
                            <FakeLinearProgressBar
                              linearProgressProps={{
                                variant: 'determinate',
                                value: 100,
                                color: 'error',
                              }}
                            />
                          )
                        }
                      />
                      <RiskGridItem
                        {...itemGridSize}
                        label={`Risk Tolerance Level`}
                        value={
                          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
                            {riskLevelTitle}
                            <ChevronRight />
                          </Box>
                        }
                      />
                    </Grid>
                    <Grid xs={12} container gap={3} wrap="nowrap">
                      <ConceptsBox xs={12} md={12} isExclusion={true} />
                    </Grid>
                  </Grid>
                </SummaryItems>
              </SummaryItems>
            </AccordionDetails>
          </SummaryAccordion>
        </Grid>
        <Grid id="channel-exclusion-list" xs={12}>
          <SummaryAccordion expanded color="black" sx={{ backgroundColor: 'white' }}>
            <AccordionSummary
              aria-controls="panel1-content"
              id="panel1-header"
              TouchRippleProps={{ style: { display: 'none' } }}
              sx={{
                cursor: 'default !important',
                '&:hover': { backgroundColor: 'transparent' },
                '&.Mui-focused, &.Mui-focusVisible': { backgroundColor: 'transparent' },
                '& .MuiAccordionSummary-content': { cursor: 'default' },
                '& .MuiTouchRipple-root': { display: 'none' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'flex-start' },
                  justifyContent: 'space-between',
                  gap: { xs: 2, md: 0 },
                  width: '100%',
                }}
              >
                <ChannelResultsHeader>
                  <Typography
                    variant="h6"
                    fontWeight={'bold'}
                    sx={(theme) => ({
                      color: theme.palette.secondary.main,
                    })}
                  >
                    Channel Exclusion List:{' '}
                    <Typography component={'span'} variant="h6" fontWeight={'thin'} color="inherit">
                      {getViewsAbbreviated(exclusionResults?.aggregates.total_number_of_channels)}{' '}
                      Total
                    </Typography>
                  </Typography>
                  <MaxLogoIcon />
                </ChannelResultsHeader>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    alignItems: { xs: 'stretch', sm: 'flex-end', md: 'flex-start' },
                    flexWrap: { xs: 'nowrap', md: 'wrap' },
                    gap: 2,
                  }}
                >
                  <PrimarySortPicker />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  px: 4,
                  pb: 4,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography component={'span'} variant="body1" fontWeight={'thin'}>
                  Below are a list of unique channels that you can exclude in your campaign
                  placement
                </Typography>
                <Box sx={{ flexShrink: 0 }}>
                  <DownloadListButton />
                </Box>
              </Box>
              <VideoTable
                rows={exclusionResults?.channel_results}
                isExclusion={true}
                isLoading={isExclusionResultLoading || isExclusionResultFetching}
                totalRows={exclusionResults?.aggregates.total_number_of_channels}
              />
            </AccordionDetails>
          </SummaryAccordion>
        </Grid>
      </Grid>
    </PaddedFormControl>
  );
};

export default StepExclusionResults;
